import { AnalyzeType } from '../../@types/samples/types';
import { Zone } from '../../@types/projects/types';
import { buildLambdaZoneCurves, buildLiaoZoneCurves } from '../utils/curveGeneration';

const hasCurvePointCount = (values: number[] | undefined, expectedCount: number) =>
  Array.isArray(values) && values.length === expectedCount;

const hasExpectedCurvesForZone = (
  zone: Zone,
  expectedCount: number,
  analysisType: AnalyzeType,
  expectedCurves: ReturnType<typeof buildLiaoZoneCurves>
) => {
  const zoneCurves =
    analysisType === AnalyzeType.LIAO
      ? zone.zone_curves?.liao_curves
      : zone.zone_curves?.lambda_curves;

  if (!zoneCurves) return false;

  const hasExpectedPointCounts = (
    hasCurvePointCount(zoneCurves.sw, expectedCount) &&
    hasCurvePointCount(zoneCurves.sg, expectedCount) &&
    hasCurvePointCount(zoneCurves.pcow, expectedCount) &&
    hasCurvePointCount(zoneCurves.height, expectedCount) &&
    hasCurvePointCount(zoneCurves.krw, expectedCount) &&
    hasCurvePointCount(zoneCurves.krow, expectedCount) &&
    hasCurvePointCount(zoneCurves.krg, expectedCount) &&
    hasCurvePointCount(zoneCurves.krog, expectedCount)
  );
  if (!hasExpectedPointCounts || !expectedCurves) return false;

  // Point counts alone cannot prove that a curve represents the currently
  // selected equations. Manual changes normally preserve the point count.
  return (
    JSON.stringify(zoneCurves.sw) === JSON.stringify(expectedCurves.sw) &&
    JSON.stringify(zoneCurves.sg) === JSON.stringify(expectedCurves.sg) &&
    JSON.stringify(zoneCurves.pcow) === JSON.stringify(expectedCurves.pcow) &&
    JSON.stringify(zoneCurves.krw) === JSON.stringify(expectedCurves.krw) &&
    JSON.stringify(zoneCurves.krow) === JSON.stringify(expectedCurves.krow) &&
    JSON.stringify(zoneCurves.krg) === JSON.stringify(expectedCurves.krg) &&
    JSON.stringify(zoneCurves.krog) === JSON.stringify(expectedCurves.krog)
  );
};

const zoneNeedsCurveRecalculation = (
  zone: Zone,
  expectedCount: number,
  gradDiff: number | null | undefined
) => {
  if (!zone.zone_values || typeof gradDiff !== 'number') {
    return false;
  }

  const expectedLiao = buildLiaoZoneCurves(zone, gradDiff, expectedCount);
  const expectedLambda = buildLambdaZoneCurves(zone, gradDiff, expectedCount);

  return (
    (expectedLiao &&
      !hasExpectedCurvesForZone(
        zone,
        expectedCount,
        AnalyzeType.LIAO,
        expectedLiao
      )) ||
    (expectedLambda &&
      !hasExpectedCurvesForZone(
        zone,
        expectedCount,
        AnalyzeType.LAMBDA,
        expectedLambda
      ))
  );
};

export const projectNeedsKrPcRecalculation = (
  zones: Zone[],
  expectedCount: number,
  gradDiff: number | null | undefined
) =>
  zones.some(zone => zoneNeedsCurveRecalculation(zone, expectedCount, gradDiff));
